<template>
  <div class="app-intro-container">
    <!-- 头部 Banner：背书与产品介绍 -->
    <header class="hero-section">
      <div class="hero-content">
        <div class="tags">
          <span class="tag">国家儿童医学中心成果转化</span>
          <span class="tag">京东健康战略合作</span>
        </div>
        <h1 class="main-title">优启星 · 儿童神经发育障碍智能干预 APP</h1>
        <p class="subtitle">
          依托北京儿童医院专家团队科研成果，专为孤独症与社交障碍儿童打造的“医-校-社-家”全场景系统化干预平台。
        </p>
      </div>
    </header>

    <!-- 权威医疗背景与合作背书 -->
    <section class="trust-section">
      <div class="section-title">
        <h2>权威背书 · 顶尖学术支撑</h2>
        <p>由北京儿童医院专家团队领衔研发，打造高度可信赖的临床级干预系统</p>
      </div>
      <div class="trust-grid">
        <div class="trust-card">
          <div class="icon-box"><i class="el-icon-s-custom"></i></div>
          <h3>专家团队领衔</h3>
          <p>首都师范大学附属北京儿童医院专家团队共同科研，融合精神科与神经疾病研究成果。</p>
        </div>
        <div class="trust-card">
          <div class="icon-box"><i class="el-icon-cpu"></i></div>
          <h3>循证医学验证</h3>
          <p>基于 ABA 行为分析与视频示范（VM）理论，多项学术研究数据证实干预有效性。</p>
        </div>
        <div class="trust-card">
          <div class="icon-box"><i class="el-icon-connection"></i></div>
          <h3>线上线下融合</h3>
          <p>结合数字游戏与线下实体搭建场景（如教室、商场），实现双管齐下泛化练习。</p>
        </div>
      </div>
    </section>

    <!-- 核心 App 功能模块展现 -->
    <section class="features-section">
      <div class="section-title">
        <h2>APP 核心干预模块</h2>
        <p>多感官刺激与低风险模拟，助力孩子能力跃升与生活技能泛化</p>
      </div>

      <div class="feature-grid">
        <!-- 模块 1：两大沙盘 -->
        <div class="feature-card">
          <div class="card-tag">核心优势</div>
          <h3>客观世界真实模拟沙盘</h3>
          <p class="feature-desc">重现真实社交场景与生活环境，改善社交沟通缺陷，提升情绪调节与睡眠质量。</p>
          <ul class="feature-points">
            <li><i class="el-icon-check"></i> 游乐场、社区等真实场景模拟</li>
            <li><i class="el-icon-check"></i> 结合对话与多感官刺激强化社交</li>
          </ul>
        </div>

        <!-- 模块 2：情景剧场 -->
        <div class="feature-card">
          <div class="card-tag">动态示范</div>
          <h3>情景剧场 (视频示范 VM)</h3>
          <p class="feature-desc">利用“动态加工优势效应”，通过动态视频示范教孩子精准识别表情与情境情绪。</p>
          <ul class="feature-points">
            <li><i class="el-icon-check"></i> 6 大基本表情识别训练</li>
            <li><i class="el-icon-check"></i> 情境技能快速泛化至现实生活</li>
          </ul>
        </div>

        <!-- 模块 3：泛生活场景 -->
        <div class="feature-card">
          <div class="card-tag">技能泛化</div>
          <h3>泛生活场景模拟训练</h3>
          <p class="feature-desc">涵盖超市购物、电话拨打、校园生活等，在安全低压的环境中熟练掌握日常技能。</p>
          <ul class="feature-points">
            <li><i class="el-icon-check"></i> 交互式模拟，提升对话与解决问题能力</li>
            <li><i class="el-icon-check"></i> 量化跟踪干预数据，效果直观可测</li>
          </ul>
        </div>

        <!-- 模块 4：训练馆小游戏 -->
        <div class="feature-card">
          <div class="card-tag">专项训练</div>
          <h3>认知与精细动作训练馆</h3>
          <p class="feature-desc">包含火柴棍、七巧板、翻牌配对等小游戏，全面提升视觉空间记忆与手眼协调能力。</p>
          <ul class="feature-points">
            <li><i class="el-icon-check"></i> 专项锻炼社交、记忆、运动、脑力</li>
            <li><i class="el-icon-check"></i> 结构化游戏设计，有效调动专注力</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- 交互展示：点击查看 APP 界面预览与详情 -->
    <section class="preview-section">
      <div class="section-title">
        <h2>App 真实界面演示</h2>
        <p>点击下方按钮，体验智能干预系统的交互展示</p>
      </div>

      <div class="interactive-container">
        <div class="image-box">
          <!-- App 场景封面缩略图 -->
          <img
              class="app-preview-img"
              :class="{ 'moved-img': isMoved }"
              src="@/assets/img/app.png"
              alt="App 交互示意"
          />

          <!-- 详情展开图/多图轮播弹窗 -->
          <transition name="fade">
            <div v-show="isMoved" class="detail-drawer">
              <div class="drawer-overlay" @click="closeImages"></div>

              <div class="drawer-content">
                <i class="el-icon-close close-btn" @click="closeImages"></i>
                <h3 class="drawer-title">APP 详细功能图解与交互展示</h3>

                <!-- 多图 3D 卡片轮播 -->
                <el-carousel :interval="4000" type="card" height="420px" class="drawer-carousel">
                  <el-carousel-item v-for="(item, index) in imgList" :key="index">
                    <div class="carousel-card-inner">
                      <img :src="item.src" :alt="item.title" class="detail-img" />
                      <div class="img-caption" v-if="item.title">{{ item.title }}</div>
                    </div>
                  </el-carousel-item>
                </el-carousel>
              </div>
            </div>
          </transition>
        </div>

        <button class="action-button" @click="moveImages">
          {{ isMoved ? '收起详情' : '点击查看 APP 详细功能图解' }}
        </button>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "YqhxAppIntro",
  data() {
    return {
      isMoved: false,
      // 多张详情图片列表（可自由按需替换/添加）
      imgList: [
        {
          src: require('@/assets/img/nlz.png'),
          title: "训练进度"
        },
        {
          src: require('@/assets/img/xs.jpg'),
          title: "生活技能训练"
        },
        {
          src: require('@/assets/img/qxjy.jpg'),
          title: "情绪与记忆力训练"
        },
        {
          src: require('@/assets/img/fp.jpg'),
          title: "记忆力与专注力训练"
        },
        {
          src: require('@/assets/img/hcg.jpg'),
          title: "手眼协调与精细动作训练"
        },
        {
          src: require('@/assets/img/qqb.jpg'),
          title: "视觉匹配与观察力训练"
        },
        {
          src: require('@/assets/img/ssfj.jpg'),
          title: "视觉搜索与细节观察"
        },
        {
          src: require('@/assets/img/cs.jpg'),
          title: "生活社交模拟训练"
        }
      ]
    };
  },
  methods: {
    moveImages() {
      this.isMoved = !this.isMoved;
    },
    closeImages() {
      this.isMoved = false;
    }
  }
};
</script>

<style scoped>

.app-intro-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #333;
  background-color: #fcfdfd;
}

/* 标题通用样式 */
.section-title {
  text-align: center;
  margin-bottom: 40px;
}

.section-title h2 {
  font-size: 2.2rem;
  color: #2c3e50;
  margin-bottom: 10px;
  font-weight: 700;
}

.section-title p {
  font-size: 1.1rem;
  color: #7f8c8d;
}

/* Banner 头部 */
.hero-section {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-radius: 16px;
  padding: 50px 30px;
  text-align: center;
  margin-bottom: 50px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
}

.tags {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 15px;
}

.tag {
  background-color: #ffffff;
  color: #2e7d32;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
}

.main-title {
  font-size: 2.5rem;
  color: #1b5e20;
  margin-bottom: 15px;
  font-weight: 800;
}

.subtitle {
  font-size: 1.2rem;
  color: #388e3c;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
}

/* 背书三列布局 */
.trust-section {
  margin-bottom: 60px;
}

.trust-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
}

.trust-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 30px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
  text-align: center;
  transition: transform 0.3s ease;
}

.trust-card:hover {
  transform: translateY(-5px);
}

.icon-box {
  font-size: 40px;
  color: #4caf50;
  margin-bottom: 15px;
}

.trust-card h3 {
  font-size: 1.3rem;
  color: #2c3e50;
  margin-bottom: 10px;
}

.trust-card p {
  color: #666;
  font-size: 0.95rem;
  line-height: 1.5;
}

/* APP 核心功能四网格 */
.features-section {
  margin-bottom: 60px;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 25px;
}

.feature-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 25px;
  border-top: 4px solid #4caf50;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  position: relative;
  display: flex;
  flex-direction: column;
}

.card-tag {
  position: absolute;
  top: 15px;
  right: 15px;
  background-color: #f0f9eb;
  color: #67c23a;
  font-size: 0.8rem;
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.feature-card h3 {
  font-size: 1.25rem;
  color: #333;
  margin-top: 10px;
  margin-bottom: 12px;
}

.feature-desc {
  font-size: 0.95rem;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.5;
  flex-grow: 1;
}

.feature-points {
  list-style: none;
  padding: 0;
  margin: 0;
}

.feature-points li {
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.feature-points li i {
  color: #4caf50;
  font-weight: bold;
}

/* 交互演示区域 */
.preview-section {
  background: #f8f9fa;
  border-radius: 16px;
  padding: 40px 20px;
  margin-bottom: 40px;
}

.interactive-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.image-box {
  position: relative;
  min-height: 350px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 25px;
  width: 100%;
}

.app-preview-img {
  width: 700px;
  max-width: 90%;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0,0,0,0.15);
  transition: all 0.5s ease;
}

/* 全屏高质感遮罩弹窗 */
.detail-drawer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drawer-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(8px);
}

.drawer-content {
  position: relative;
  z-index: 10;
  width: 88%;
  max-width: 1050px;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 20px;
  padding: 30px 40px 40px 40px;
  box-shadow: 0 20px 50px rgba(0,0,0,0.3);
}

.drawer-title {
  font-size: 1.5rem;
  color: #1e293b;
  text-align: center;
  margin-bottom: 24px;
  font-weight: 700;
}

/* 轮播卡片内框与图片样式 */
.carousel-card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  background-color: #000;
}

.detail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.img-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 16px;
  background: linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%);
  color: #ffffff;
  font-size: 0.95rem;
  text-align: center;
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 24px;
  font-size: 28px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 12;
}

.close-btn:hover {
  color: #f44336;
  transform: rotate(90deg);
}

.action-button {
  padding: 12px 32px;
  font-size: 1rem;
  font-weight: 600;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  transition: all 0.3s ease;
}

.action-button:hover {
  background-color: #43a047;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4);
}

/* 淡入淡出动画 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* ============ 移动端适配 ============ */
@media (max-width: 767px) {
  .app-intro-container {
    padding: 12px;
  }

  .hero-section {
    padding: 30px 16px;
    margin-bottom: 38px;
  }

  .tags {
    flex-wrap: wrap;
    gap: 8px;
  }

  .main-title {
    font-size: 1.65rem;
    line-height: 1.4;
  }

  .subtitle {
    font-size: 1rem;
    line-height: 1.7;
  }

  .section-title {
    margin-bottom: 26px;
  }

  .section-title h2 {
    font-size: 1.5rem;
  }

  .section-title p {
    font-size: 0.95rem;
  }

  .trust-section,
  .features-section {
    margin-bottom: 44px;
  }

  .feature-grid {
    gap: 18px;
  }

  .preview-section {
    padding: 30px 10px;
    margin-bottom: 24px;
  }

  .image-box {
    min-height: 260px;
    margin-bottom: 18px;
  }

  .drawer-content {
    width: 94%;
    padding: 24px 12px 26px;
  }

  .drawer-title {
    font-size: 1.15rem;
    margin-bottom: 14px;
  }

  .close-btn {
    top: 12px;
    right: 12px;
    font-size: 24px;
  }

  .action-button {
    padding: 11px 24px;
    font-size: 0.95rem;
  }
}
</style>